import React, { useRef, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import "../assets/css/pages/Blog.css";
import "../assets/css/Responsive/Blog_Responsive.css";
import { fetchBlogList } from "../api/blog";
import Lenis from "@studio-freight/lenis";
import { useSeo } from "../contexts/SeoContext";

// ---- helpers ----
const sanitize = (value) => (typeof value === "string" ? value.trim() : "");

const sanitizeImage = (image) => {
    if (!image) return null;
    const url = sanitize(image?.url || "");
    if (!url) return null;
    return { url, alt: sanitize(image?.alt || "") };
};

/**
 * Lenis smooth scroll (same style as other pages)
 */
const useLenis = () => {
    useEffect(() => {
        if (typeof window === "undefined") return;

        document.documentElement.style.scrollBehavior = "auto";

        const lenis = new Lenis({
            duration: 1.2,
            easing: (t) => 1 - Math.pow(1 - t, 3),
            smoothWheel: true,
            smoothTouch: false,
        });

        const raf = (time) => {
            lenis.raf(time);
            requestAnimationFrame(raf);
        };

        requestAnimationFrame(raf);

        return () => {
            lenis.destroy();
        };
    }, []);
};

export default function BlogSection() {
    const [searchParams, setSearchParams] = useSearchParams();
    const scrollerRef = useRef(null);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isFiltering, setIsFiltering] = useState(false);
    const [error, setError] = useState(null);
    const isInitialMount = useRef(true);
    const [isMobile, setIsMobile] = useState(false);

    // 🔥 Initialize Lenis
    useLenis();

    // Set page title
    useEffect(() => {
        document.title = "Blog | Vancouver Lookout";
    }, []);

    // Detect screen size for responsive pagination
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth <= 768); // 768px breakpoint for mobile
        };

        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);

        return () => {
            window.removeEventListener("resize", checkScreenSize);
        };
    }, []);

    // Get category and page from URL params
    const categorySlug = searchParams.get("category") || "all";
    const currentPage = parseInt(searchParams.get("page") || "1", 10);

    useEffect(() => {
        let isMounted = true;

        const loadBlogs = async () => {
            try {
                // Only show full page loading on initial mount
                if (isInitialMount.current) {
                    setLoading(true);
                } else {
                    // Show subtle filtering state for subsequent loads
                    setIsFiltering(true);
                }

                // Set per_page to 5 for mobile, null (default) for desktop
                const perPage = isMobile ? 5 : null;
                const response = await fetchBlogList(categorySlug, currentPage, perPage);

                if (!isMounted) return;

                if (response) {
                    setData(response);
                    setError(null);
                } else {
                    setError("No blog data available.");
                }
            } catch (err) {
                if (!isMounted) return;
                setError("Unable to load blogs at the moment.");
                setData(null);
            } finally {
                if (isMounted) {
                    setLoading(false);
                    setIsFiltering(false);
                    isInitialMount.current = false;
                }
            }
        };

        loadBlogs();

        return () => {
            isMounted = false;
        };
    }, [categorySlug, currentPage, isMobile]);

    // Handle category filter change
    const handleCategoryChange = (slug) => {
        // Prevent action if already filtering or same category
        if (isFiltering || categorySlug === slug) return;

        const newParams = new URLSearchParams(searchParams);
        newParams.set("category", slug);
        newParams.set("page", "1"); // Reset to page 1 when category changes
        setSearchParams(newParams);

        // Scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Handle page change
    const handlePageChange = (page) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("page", page.toString());
        setSearchParams(newParams);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Scroller effect for category filters
    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;

        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;

        const handleMouseDown = (e) => {
            isDown = true;
            scroller.classList.add("active");
            startX = e.pageX - scroller.offsetLeft;
            scrollLeft = scroller.scrollLeft;
        };

        const handleMouseLeave = () => {
            isDown = false;
            scroller.classList.remove("active");
        };

        const handleMouseUp = () => {
            isDown = false;
            scroller.classList.remove("active");
        };

        const handleMouseMove = (e) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - scroller.offsetLeft;
            const walk = (x - startX) * 1.5;
            scroller.scrollLeft = scrollLeft - walk;
        };

        scroller.addEventListener("mousedown", handleMouseDown);
        scroller.addEventListener("mouseleave", handleMouseLeave);
        scroller.addEventListener("mouseup", handleMouseUp);
        scroller.addEventListener("mousemove", handleMouseMove);

        return () => {
            scroller.removeEventListener("mousedown", handleMouseDown);
            scroller.removeEventListener("mouseleave", handleMouseLeave);
            scroller.removeEventListener("mouseup", handleMouseUp);
            scroller.removeEventListener("mousemove", handleMouseMove);
        };
    }, [data]);

    // AOS (Animate On Scroll) hook with stagger (About-style)
    useEffect(() => {
        if (!data || loading) return;

        let observer = null;
        const observedCards = [];

        const timer = setTimeout(() => {
            const observerOptions = {
                threshold: 0.1,
                rootMargin: "0px 0px -80px 0px",
            };

            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    requestAnimationFrame(() => {
                        if (!entry.isIntersecting) return;

                        const card = entry.target;
                        if (card.classList.contains("aos-animate")) return;

                        const staggerIndex =
                            parseInt(card.getAttribute("data-stagger-index")) || 0;
                        const delay = staggerIndex * 80; // tweak as you like

                        setTimeout(() => {
                            card.classList.add("aos-animate");
                        }, delay);
                    });
                });
            }, observerOptions);

            // Support multiple AOS animation classes for cards if needed
            const AOS_SELECTORS = [
                ".blog-card.aos-fade-up",
                ".blog-card.aos-fade-in",
                ".blog-card.aos-fade-left",
                ".blog-card.aos-fade-right",
                ".blog-card.aos-zoom-in",
            ];

            const cardsToObserve = document.querySelectorAll(
                AOS_SELECTORS.join(",")
            );

            cardsToObserve.forEach((card) => {
                if (!card.classList.contains("aos-animate")) {
                    observer.observe(card);
                    observedCards.push(card);
                }
            });
        }, 100);


        return () => {
            clearTimeout(timer);
            if (observer) {
                observedCards.forEach((card) => {
                    if (card) observer.unobserve(card);
                });
            }
        };
    }, [data, loading]);

    // Always render page structure - show content immediately when available
    if (error || !data) {
        return (
            <div className="page-error">
                {/* {error || "No blog data available."} */}
            </div>
        );
    }

    const { eyebrow, title, categories = [], posts = [], pagination = {} } = data;

    const {
        current_page = 1,
        total_pages = 1,
        has_next = false,
        has_prev = false,
    } = pagination;

    // Use API pagination directly - API now properly handles per_page parameter
    const displayedPosts = posts;
    const finalTotalPages = total_pages;
    const finalHasNext = has_next;
    const finalHasPrev = has_prev;

    const goPrev = () => {
        if (finalHasPrev && current_page > 1) {
            handlePageChange(current_page - 1);
        }
    };

    const goNext = () => {
        if (finalHasNext && current_page < finalTotalPages) {
            handlePageChange(current_page + 1);
        }
    };

    // Generate page numbers array (show max 5 pages)
    const getPageNumbers = () => {
        const pages = [];
        const maxPages = Math.min(finalTotalPages, 5);
        let startPage = Math.max(1, current_page - 2);
        let endPage = Math.min(finalTotalPages, startPage + maxPages - 1);

        if (endPage - startPage < maxPages - 1) {
            startPage = Math.max(1, endPage - maxPages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    const pageNumbers = getPageNumbers();
    const showPagination = finalTotalPages > 1;

    return (
        <section className="blogs-section">
            <div className="blog-section-inner">
                {/* Loading overlay for filtering - subtle, doesn't replace content */}
                {isFiltering && (
                    <div className="blogs-filtering-overlay">
                        <div className="blogs-filtering-spinner"></div>
                    </div>
                )}

                {(eyebrow || title) && (
                    <div className="blogs-head">
                        {eyebrow && <p className="blogs-eyebrow">{eyebrow}</p>}
                        {title && <h1 className="blogs-title">{title}</h1>}

                        {categories.length > 0 && (
                            <div className="blogs-filter-wrapper">
                                <div
                                    className={`blogs-filters auto-scroll ${isFiltering ? "filtering" : ""
                                        }`}
                                    ref={scrollerRef}
                                >
                                    {categories.map((category) => {
                                        const isActive = categorySlug === category.slug;
                                        return (
                                            <button
                                                key={category.id}
                                                onClick={() =>
                                                    handleCategoryChange(category.slug)
                                                }
                                                className={isActive ? "active" : "inactive"}
                                                disabled={isFiltering}
                                            >
                                                {category.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className={`container ${isFiltering ? "filtering" : ""}`}>
                    {displayedPosts.length > 0 ? (
                        <div className="blogs-grid">
                            {displayedPosts.map((post, index) => {
                                const image = sanitizeImage(post.featured_image);
                                const postTitle = sanitize(post.title || "");
                                const excerpt = sanitize(post.excerpt || "");
                                const categoriesStr = Array.isArray(post.categories)
                                    ? post.categories.join(", ")
                                    : "";
                                const slug = sanitize(post.slug || "");

                                if (!postTitle || !slug) return null;

                                return (
                                    <Link
                                        to={`/blog/${slug}`}
                                        className="blog-card aos-fade-up"
                                        key={post.id}
                                        data-stagger-index={index} // 👈 used by AOS stagger
                                    >
                                        {image && (
                                            <div className="blog-img">
                                                <img
                                                    src={image.url}
                                                    alt={image.alt || postTitle}
                                                />
                                            </div>
                                        )}

                                        <div className="blog-content">
                                            {categoriesStr && (
                                                <p className="blog-meta">{categoriesStr}</p>
                                            )}

                                            <div className="blog-sub-content">
                                                {postTitle && (
                                                    <h3
                                                        className="blog-title"
                                                        dangerouslySetInnerHTML={{ __html: postTitle }}
                                                    />
                                                )}
                                                {excerpt && (
                                                    <p
                                                        className="blog-text"
                                                        dangerouslySetInnerHTML={{ __html: excerpt }}
                                                    />
                                                )}
                                            </div>

                                            <div className="blog-link">
                                                <span>
                                                    READ MORE
                                                    <span className="arrow-wrap">
                                                        <svg
                                                            className="arrow-icon arrow-icon-1"
                                                            width="25"
                                                            height="25"
                                                            viewBox="0 0 25 25"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M5.89844 19.5L18.0507 7M18.0507 7H8.93649M18.0507 7V16.375"
                                                                stroke="#086493"
                                                                strokeWidth="1.6203"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                        <svg
                                                            className="arrow-icon arrow-icon-2"
                                                            width="25"
                                                            height="25"
                                                            viewBox="0 0 25 25"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M5.89844 19.5L18.0507 7M18.0507 7H8.93649M18.0507 7V16.375"
                                                                stroke="#086493"
                                                                strokeWidth="1.6203"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="page-error">No blog posts found.</div>
                    )}

                    {showPagination && (
                        <div className="pagination">
                            {/* LEFT ARROW */}
                            <svg
                                width="23"
                                height="23"
                                viewBox="0 0 23 23"
                                fill="none"
                                className={`arrow ${!finalHasPrev || current_page === 1 ? "disabled" : ""
                                    }`}
                                onClick={goPrev}
                                style={{
                                    cursor:
                                        finalHasPrev && current_page > 1
                                            ? "pointer"
                                            : "not-allowed",
                                }}
                            >
                                <path
                                    d="M9.30223 7.44141L5.5813 11.1623M5.5813 11.1623L9.30223 14.8833M5.5813 11.1623L16.7441 11.1623"
                                    stroke="currentColor"
                                    strokeWidth="1.39535"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>

                            {/* PAGE NUMBERS */}
                            {pageNumbers.map((num) => (
                                <a
                                    key={num}
                                    className={current_page === num ? "active" : "inactive"}
                                    onClick={() => handlePageChange(num)}
                                    style={{ cursor: "pointer" }}
                                >
                                    {num}
                                </a>
                            ))}

                            {/* RIGHT ARROW */}
                            <svg
                                width="23"
                                height="23"
                                viewBox="0 0 23 23"
                                fill="none"
                                className={`arrow ${!finalHasNext || current_page === finalTotalPages
                                    ? "disabled"
                                    : ""
                                    }`}
                                onClick={goNext}
                                style={{
                                    cursor:
                                        finalHasNext && current_page < finalTotalPages
                                            ? "pointer"
                                            : "not-allowed",
                                }}
                            >
                                <path
                                    d="M13.0235 14.8842L16.7444 11.1632M16.7444 11.1632L13.0235 7.44232M16.7444 11.1632L5.58159 11.1632"
                                    stroke="currentColor"
                                    strokeWidth="1.39535"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
